import type { ReactNode } from 'react';
import { useAuth } from '@/lib/auth/useAuth';
import { PERMISSION_LABELS, type Permission } from './permissions';

export interface GateResult {
  allowed: boolean;
  /** Tooltip reason when not allowed, e.g. "Requires permission to publish…". */
  reason: string | null;
}

/** Predicate hook used to hide nav or disable actions (spec §4). */
export function useGate(permission: Permission | Permission[]): GateResult {
  const { can } = useAuth();
  const perms = Array.isArray(permission) ? permission : [permission];
  const missing = perms.filter((p) => !can(p));
  if (missing.length === 0) return { allowed: true, reason: null };
  const labels = missing.map((p) => PERMISSION_LABELS[p]).join(', ');
  return { allowed: false, reason: `Requires permission to ${labels}.` };
}

/**
 * Hide content a role cannot access. For read-gating whole screens/nav entries.
 * (Action buttons should be DISABLED, not hidden — use PermissionButton in lib/ui.)
 */
export function Gate({
  permission,
  children,
  fallback = null,
}: {
  permission: Permission | Permission[];
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { allowed } = useGate(permission);
  return <>{allowed ? children : fallback}</>;
}
